import { useEffect, useState } from "react";
import { Button, Popconfirm, Table, TableColumnsType } from "antd";
import { DeleteOutlined, LockOutlined, EditOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { AuthenticationState, UserInterface } from "../../interfaces/users";
import { api } from "../../config";
import { UserModal } from "./UserModal";
import { deleteUser } from "../../utils";
import { PermissionsModal } from "./PermissionsModal";

import { SearchFilter } from "../Shared/Components/Filters/SearchFilter";

import {
  ModulePermission,
  userHasPermission,
} from "../../utils/helpers/userHelpers";

interface DataType {
  key: React.Key;
  id: number;
  fullName: string;
  email: string;
  operation: string;
}

export const Users = () => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const { data, isFetching, refetch } = useQuery("users", () =>
    api(token).get("/users")
  );
  const [tableData, setTableData] = useState(data);

  const handleDelete = async (userId: number) => {
    await deleteUser(token, userId);
    refetch();
  };

  const handleOnOpen = (userSent: any, type?: "permissions" | "info") => {
    setSelectedUser(userSent);
    if (type === "info") {
      setShowEditModal(true);
    } else if (type === "permissions") {
      setShowPermissionsModal(true);
    }
  };

  const userCanEditInfo = (user: Partial<UserInterface> | undefined) => {
    if (!user?.modules) return false;
    const isUsersAdmin = userHasPermission(user, {
      users: ModulePermission.ADMIN,
    });
    const isUsersUser = userHasPermission(user, {
      users: ModulePermission.USER,
    });
    if (isUsersAdmin || isUsersUser) return true;
    return false;
  };

  const defaultColumns: TableColumnsType<DataType> = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: (
        <SearchFilter
          placeholder={"Nome"}
          data={data}
          setData={setTableData}
          field={"fullName"}
        />
      ),
      dataIndex: "fullName",
      width: "40%",
    },
    {
      title: (
        <SearchFilter
          placeholder={"Email"}
          data={data}
          setData={setTableData}
          field={"email"}
        />
      ),
      dataIndex: "email",
    },
    {
      title: "Ações",
      dataIndex: "operation",
      render: (_: any, record: any) =>
        tableData ? (
          <>
            <Popconfirm
              title="Deseja realmente apagar?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button
                className="m-1 p-1 mt-2"
                size="small"
                danger
                shape="circle"
                icon={<DeleteOutlined />}
                disabled={
                  !userHasPermission(user, {
                    users: ModulePermission.ADMIN,
                  })
                }
              />
            </Popconfirm>
            <Button
              className="m-1 p-2 mt-2 mr-5"
              disabled={!userCanEditInfo(user)}
              size="small"
              onClick={() => handleOnOpen(record, "info")}
              icon={<EditOutlined />}
            />
            <Button
              className="m-2 p-2 mr-5"
              size="small"
              icon={<LockOutlined />}
              onClick={() => handleOnOpen(record, "permissions")}
              disabled={
                !userHasPermission(user, {
                  users: ModulePermission.ADMIN,
                })
              }
            />
          </>
        ) : null,
    },
  ];

  useEffect(() => {
    setTableData(data);
  }, [data]);

  return (
    <div>
      <PermissionsModal
        isOpen={showPermissionsModal}
        refetch={refetch}
        onClose={() => setShowPermissionsModal(false)}
        user={selectedUser}
      />
      <UserModal
        user={selectedUser}
        refetch={refetch}
        showEditModal={showEditModal}
        onClose={() => setShowEditModal(false)}
      />
      <Button
        onClick={() => handleOnOpen(undefined, "info")}
        type="primary"
        style={{
          marginBottom: 16,
        }}
      >
        Adicionar Usuário
      </Button>
      <Table<DataType>
        rowKey="id"
        loading={isFetching}
        bordered
        dataSource={tableData}
        columns={defaultColumns}
        pagination={{ position: ["bottomCenter"] }}
      />
    </div>
  );
};
export default Users;
